import { Component, computed, inject, NgZone, signal } from '@angular/core';
import { MainPageModule } from "../main.page/main.page.module";
import { SelectedAttributeService } from '../main.page/services/selected-attribute.service';
import { MatDialog } from '@angular/material/dialog';
import { EngravingTypesService } from 'src/app/services/engraving-types/engraving-types.service';
import { DesignsService } from 'src/app/services/designs/designs.service';
import { FormsService } from 'src/app/services/forms/forms.service';
import { ProfileGroupsService } from 'src/app/services/profile-groups/profile-groups.service';
import { ProfileWeightsService } from 'src/app/services/profile-weights/profile-weights.service';
import { MaterialsService } from 'src/app/services/materials/materials.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { WidthsService } from 'src/app/services/widths/widths.service';
import { LoadingIndicatorModule } from 'src/app/modules/loading-indicator/loading-indicator.module';
import { NonClosableErrorDialogComponent } from '../main.page/components/non-closable-error.dialog/non-closable-error.dialog.component';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { assert } from 'src/app/utils/assert.util';
import { SkuMapService } from 'src/app/services/sku-map/sku-map.service';
import { CheckoutService } from '../main.page/services/checkout.service';
import { OpenReplayService } from 'src/app/services/open-replay/open-replay.service';
import { environment } from 'src/environments/environment';
import { IframeService } from 'src/app/services/iframe/iframe.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneSizesService } from 'src/app/services/stone-sizes/stone-sizes.service';
import { StoneArrangementsService } from 'src/app/services/stone-arrangements/stone-arrangements.service';
import { StoneTypesService } from 'src/app/services/stone-types/stone-types.service';
import { StoneSettingsService } from 'src/app/services/stone-settings/stone-settings.service';
import { StoneShapesService } from 'src/app/services/stone-shapes/stone-shapes.service';
import { CustomerThemeService } from 'src/app/services/customer-theme/customer-theme.service';

@Component({
  selector: 'app-share-root',
  templateUrl: './share-root.component.html',
  styleUrl: './share-root.component.scss',
  standalone: true,
  imports: [MainPageModule, LoadingIndicatorModule]
})
export class ShareRootComponent {
  private zone = inject(NgZone);
  private openReplayService = inject(OpenReplayService);
  private checkoutService = inject(CheckoutService);
  private skuMapService = inject(SkuMapService);
  private configuredItemService = inject(ConfiguredItemService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private dialog = inject(MatDialog);
  private engravingTypesService = inject(EngravingTypesService);
  private loadingService = inject(LoadingService);
  private iframeService = inject(IframeService);
  private customerThemeService = inject(CustomerThemeService);
  private apiServices = [inject(DesignsService), inject(FormsService), inject(ProfileGroupsService), inject(ProfileWeightsService), inject(MaterialsService), inject(SizesService), inject(WidthsService), inject(EngravingTypesService), inject(StoneArrangementsWithSettingsService), inject(StoneTypesWithShapesService), inject(StoneSizesService), inject(StoneArrangementsService), inject(StoneTypesService), inject(StoneSettingsService), inject(StoneShapesService)];
  isLoaded = signal(false);
  loadingServiceIsLoading: boolean = false;
  sku = signal<string | null>(null);

  constructor() {
    this.loadingService.isLoading.subscribe((isLoading) => {
      this.loadingServiceIsLoading = isLoading;
    });
  }

  async ngOnInit() {
    await Promise.all(this.apiServices.map(s =>s.cacheData(null)));
    const url = new URL(window.location.href);

    try {
      await this.setupSKUWithURL(url);
      if (this.checkoutService.isRetailerOnly()) {
        const retailerIframeID = this.checkoutService.retailerIframeID;
        assert(retailerIframeID, 'No retailer iframe ID');
        await this.customerThemeService.cacheTheme({ iframeID: retailerIframeID });
      }
      if (this.checkoutService.analyticsAllowed()) {
        this.zone.runOutsideAngular(() => {
          const openReplayKey = this.checkoutService.isRetailerOnly() ? environment.openReplayIframeKey : environment.openReplayKey;
          const openReplayIngestPoint = environment.openReplayIngestPoint;
          if (openReplayKey && openReplayIngestPoint) {
            this.openReplayService.init(openReplayKey, openReplayIngestPoint);
          }

          this.openReplayService.sendMetaData('StyleSzQtrSz', `${this.sku()}`);
        });
      }

    } catch(e) {
      console.error(e);
      this.dialog.open(NonClosableErrorDialogComponent, { disableClose: true, maxWidth: 400, data: 'The provided SKU is invalid. Please close this tab and try again. If this problem persists, please contact customer service.' });
    } finally {
      this.isLoaded.set(true);
    }
  }

  async setupSKUWithURL(url: URL) {
    const engravingText = url.searchParams.get('et');
    const engravingType = url.searchParams.get('e');
    const skuString = url.searchParams.get('sku');
    const po = url.searchParams.get('po');
    const size = url.searchParams.get('size');
    const isQuarterSize = url.searchParams.get('isQuarterSize') === 'true';
    const retailerID = url.searchParams.get('retailerID');

    const decodedEngravingText = !engravingText ? null : decodeURIComponent(engravingText);
    const decodedEngravingType = !engravingType ? null : this.engravingTypesService.values().find(e => e.shortName === engravingType) ?? null;
    const decodedPO = !po ? null : decodeURIComponent(po);

    assert(skuString, 'No sku provided');
    assert(size, 'No size provided');

    if (retailerID) {
      this.checkoutService.retailerIframeID = decodeURIComponent(retailerID) ?? null;
    }

    if (!this.configuredItemService.isCustomStyle(skuString)) {
      const style = skuString.replace(/\d{2}\.?\d?$/, '');
      const mapEntry = await this.skuMapService.get(style);
      assert(mapEntry, 'Could not map halo style to ring builder style');
      await this.selectedAttributeService.setConfiguredItemFromStyleWithEngravingAndPO(mapEntry.ringBuilderSKU, decodedEngravingText, decodedEngravingType, decodedPO, +size, isQuarterSize, false);
      const pricingSize = this.selectedAttributeService.configuredItem().size;
      assert(pricingSize, 'Size was null');
      this.selectedAttributeService.pricingObject.set({ style: mapEntry?.halosku ?? style, size: pricingSize });
      this.sku.set(this.configuredItemService.buildHaloSKU(style, +size + (isQuarterSize ? 0.25 : 0) , false));
    } else {
      await this.selectedAttributeService.setConfiguredItemFromStyleWithEngravingAndPO(skuString, decodedEngravingText, decodedEngravingType, decodedPO, +size, isQuarterSize, false);
      const pricingSize = this.selectedAttributeService.configuredItem().size;
      assert(pricingSize, 'Size was null');
      this.selectedAttributeService.pricingObject.set({ style: skuString, size: pricingSize });
      this.sku.set(this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
    }
  }
}
